import { RefObject, useEffect } from "react";

interface UseTimelineStickyEventTitleArgs {
  barRef: RefObject<HTMLElement>;
  titleRef: RefObject<HTMLElement>;
  titleLeftPadding: number;
  titleRightPadding: number;
  refreshKey: string;
}

type StickyTitleUpdater = () => void;

interface ScrollRegistryEntry {
  callbacks: Set<StickyTitleUpdater>;
  rafId: number;
  onScroll: () => void;
}

const scrollRegistry = new WeakMap<HTMLElement, ScrollRegistryEntry>();

const getScrollRegistryEntry = (
  scrollAncestor: HTMLElement
): ScrollRegistryEntry => {
  const existing = scrollRegistry.get(scrollAncestor);
  if (existing) return existing;

  const entry: ScrollRegistryEntry = {
    callbacks: new Set<StickyTitleUpdater>(),
    rafId: 0,
    onScroll: () => {
      if (entry.rafId) return;
      entry.rafId = window.requestAnimationFrame(() => {
        entry.rafId = 0;
        entry.callbacks.forEach((callback) => callback());
      });
    },
  };

  scrollRegistry.set(scrollAncestor, entry);
  scrollAncestor.addEventListener("scroll", entry.onScroll, {
    passive: true,
  });
  return entry;
};

const registerStickyTitleUpdater = (
  scrollAncestor: HTMLElement,
  callback: StickyTitleUpdater
) => {
  const entry = getScrollRegistryEntry(scrollAncestor);
  entry.callbacks.add(callback);

  return () => {
    entry.callbacks.delete(callback);
    if (entry.callbacks.size === 0) {
      scrollAncestor.removeEventListener("scroll", entry.onScroll);
      if (entry.rafId) window.cancelAnimationFrame(entry.rafId);
      scrollRegistry.delete(scrollAncestor);
    }
  };
};

/**
 * Imperative horizontal "sticky" for event titles. CSS sticky is unreliable
 * inside absolutely positioned event bars that use clipping. The hook keeps
 * the workaround contained and shares one rAF-throttled scroll listener per
 * scroll container instead of one DOM listener per event card.
 */
export const useTimelineStickyEventTitle = ({
  barRef,
  titleRef,
  titleLeftPadding,
  titleRightPadding,
  refreshKey,
}: UseTimelineStickyEventTitleArgs) => {
  useEffect(() => {
    const bar = barRef.current;
    const title = titleRef.current;
    if (!bar || !title) return;

    let scrollAncestor: HTMLElement | null = bar.parentElement;
    while (scrollAncestor) {
      const overflowX = window.getComputedStyle(scrollAncestor).overflowX;
      if (overflowX === "auto" || overflowX === "scroll") break;
      scrollAncestor = scrollAncestor.parentElement;
    }
    if (!scrollAncestor) return;

    const update = () => {
      const barRect = bar.getBoundingClientRect();
      const scrollRect = scrollAncestor!.getBoundingClientRect();
      const barLeftInScroll = barRect.left - scrollRect.left;

      // Reset transform before measuring so scrollWidth reflects the true
      // intrinsic content width, not a layout influenced by a stale shift.
      title.style.transform = "";
      const titleSpan = title.firstElementChild as HTMLElement | null;
      const textWidth = titleSpan ? titleSpan.scrollWidth : 0;

      const innerWidth = Math.max(
        0,
        barRect.width - titleLeftPadding - titleRightPadding
      );
      const textRightLimit = Math.max(
        0,
        barRect.width - titleRightPadding - titleLeftPadding - textWidth
      );
      const maxShift = Math.min(innerWidth, textRightLimit);

      let shift = 0;
      if (barLeftInScroll < 0 && maxShift > 0) {
        shift = Math.min(-barLeftInScroll, maxShift);
      }
      title.style.transform = shift > 0 ? `translateX(${shift}px)` : "";
    };

    update();
    return registerStickyTitleUpdater(scrollAncestor, update);
  }, [barRef, titleRef, titleLeftPadding, titleRightPadding, refreshKey]);
};
